import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { GitMerge, Loader2, Search, X, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { getCompanyDisplayName, getContactFullName } from '../utils/companyDisplayName';
import { lastDigits } from '../utils/phoneUtils';

/**
 * Fase 3 (Contatos 3D) — MergeContactsModal.
 *
 * Mescla 2 contatos da mesma pessoa. Master absorve FKs e dados da
 * duplicate. Backend: rpc_merge_contacts.
 *
 * Modos de uso:
 * - manual: receber masterContact prop e oferecer autocomplete para
 *   escolher a duplicate (ou vice-versa). Para esta fase aceita apenas
 *   {masterContact, duplicateContact} pré-definidos via prop ou via
 *   busca (caso só master seja fornecido).
 * - sequencial: após merge, oferecer transfer para mover o master para
 *   a empresa da duplicata (caso o usuário queira fechar o vínculo
 *   antigo e abrir o novo).
 */

const ContactCard = ({ label, contact, badge, fields = [] }) => {
  if (!contact) return null;
  const fullName = getContactFullName(contact) || '(sem nome)';
  const empresa = contact.empresas;
  return (
    <div className="bg-white border border-slate-200 rounded p-2.5 text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
        {badge}
      </div>
      <div className="font-semibold text-slate-800">{fullName}</div>
      {empresa && (
        <div className="text-slate-500 text-[11px] mt-0.5">{getCompanyDisplayName(empresa)}</div>
      )}
      {contact.title && <div className="text-slate-600 mt-0.5">{contact.title}</div>}
      {contact.email && <div className="text-slate-500 truncate" title={contact.email}>{contact.email}</div>}
      {contact.mobile && <div className="text-slate-500">{contact.mobile}</div>}
      {contact.phone && <div className="text-slate-500">{contact.phone}</div>}
      {contact.eh_decisor && <div className="text-emerald-700 mt-0.5">⭐ Decisor</div>}
      {contact.apollo_person_id && <div className="text-violet-700 mt-0.5">Apollo ✓</div>}
      {fields.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[11px]">
          {fields.map((f, i) => <li key={i} className="text-slate-600">{f}</li>)}
        </ul>
      )}
    </div>
  );
};

const MergeContactsModal = ({ masterContact, duplicateContact: dupInitial, onClose, onMerged }) => {
  const [duplicate, setDuplicate] = useState(dupInitial || null);
  const [dupQuery, setDupQuery] = useState('');
  const [dupResults, setDupResults] = useState([]);
  const [searchingDup, setSearchingDup] = useState(false);
  const [counts, setCounts] = useState(null);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && !merging && !transferring) onClose?.(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose, merging, transferring]);

  // Autocomplete de duplicata por nome/email/telefone
  useEffect(() => {
    if (duplicate) return undefined;
    if (!dupQuery || dupQuery.trim().length < 2) {
      setDupResults([]);
      return undefined;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearchingDup(true);
      const q = dupQuery.trim();
      const tail = lastDigits(q, 8);
      let or = `first_name.ilike.%${q}%,last_name.ilike.%${q}%,email.ilike.%${q}%`;
      if (tail.length >= 8) or += `,mobile.ilike.%${tail}%,phone.ilike.%${tail}%`;
      const { data } = await supabase
        .from('contatos')
        .select('id, first_name, last_name, email, mobile, phone, title, eh_decisor, apollo_person_id, empresa_id, contact_nature, empresas!contatos_empresa_id_fkey(id, company_name, short_name, empresa)')
        .or(or)
        .neq('id', masterContact?.id)
        .neq('contact_nature', 'functional_address')
        .limit(8);
      setDupResults(data || []);
      setSearchingDup(false);
    }, 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [dupQuery, duplicate, masterContact?.id]);

  // Carrega contagens de dependências da duplicata
  const loadCounts = useCallback(async (dupId) => {
    if (!dupId) return;
    const queries = await Promise.all([
      supabase.from('emails_gmail').select('id', { count: 'exact', head: true }).eq('contato_id', dupId),
      supabase.from('historico_interacoes').select('id', { count: 'exact', head: true }).eq('contato_id', dupId),
      supabase.from('mensagens_whatsapp').select('id', { count: 'exact', head: true }).eq('contato_id', dupId),
      supabase.from('contact_employments').select('id', { count: 'exact', head: true }).eq('contact_id', dupId),
      supabase.from('ofertas').select('id', { count: 'exact', head: true }).eq('contato_negociador_id', dupId),
    ]);
    setCounts({
      emails: queries[0].count || 0,
      interacoes: queries[1].count || 0,
      whatsapp: queries[2].count || 0,
      employments: queries[3].count || 0,
      ofertas_negociador: queries[4].count || 0,
    });
  }, []);

  useEffect(() => { if (duplicate?.id) loadCounts(duplicate.id); }, [duplicate?.id, loadCounts]);

  const fieldsToFill = useMemo(() => {
    if (!masterContact || !duplicate) return [];
    const f = [];
    const checkField = (label, key) => {
      const m = masterContact[key];
      const d = duplicate[key];
      if ((m == null || m === '') && d != null && d !== '') f.push(`${label}: ← preenche do duplicata`);
    };
    checkField('Sobrenome', 'last_name');
    checkField('Cargo', 'title');
    checkField('Celular', 'mobile');
    checkField('Telefone', 'phone');
    checkField('Email', 'email');
    checkField('LinkedIn', 'person_linkedin_url');
    checkField('CPF', 'cpf');
    checkField('Senioridade', 'senioridade');
    checkField('Departamento', 'departamento');
    checkField('Apollo Person ID', 'apollo_person_id');
    if (!masterContact.eh_decisor && duplicate.eh_decisor) f.push('Decisor: ← duplicata era decisor, master vira decisor');
    if (masterContact.observacao && duplicate.observacao && masterContact.observacao !== duplicate.observacao) {
      f.push('Observação: ← concatenada com [Merge]');
    }
    return f;
  }, [masterContact, duplicate]);

  const handleMerge = async () => {
    if (!duplicate?.id) { setError('Selecione a duplicata'); return; }
    setError(null);
    setMerging(true);
    try {
      const { data, error: err } = await supabase.rpc('rpc_merge_contacts', {
        p_master_id: masterContact.id,
        p_duplicate_id: duplicate.id,
      });
      if (err) throw err;
      setReport(data);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setMerging(false);
    }
  };

  const handleTransferAfter = async () => {
    if (!duplicate?.empresa_id || !masterContact?.id) return;
    setError(null);
    setTransferring(true);
    try {
      const { error: err } = await supabase.rpc('rpc_transfer_contact_to_company', {
        p_contact_id: masterContact.id,
        p_new_empresa_id: duplicate.empresa_id,
        p_old_empresa_id: masterContact.empresa_id || null,
        p_new_title: duplicate.title || null,
        p_new_email: duplicate.email || null,
      });
      if (err) throw err;
      onMerged?.({ ...report, transferred: true });
      onClose?.();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setTransferring(false);
    }
  };

  const handleFinishWithoutTransfer = () => {
    onMerged?.(report);
    onClose?.();
  };

  if (!masterContact) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Mesclar contatos"
      onClick={(e) => { if (e.target === e.currentTarget && !merging && !transferring) onClose?.(); }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <GitMerge size={16} className="text-violet-600" />
            <h3 className="text-sm font-semibold text-slate-800">Mesclar contatos</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={merging || transferring}
            aria-label="Fechar"
            className="p-1 rounded hover:bg-slate-200 text-slate-500 disabled:opacity-50"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {!report && (
            <>
              {!duplicate && (
                <div className="mb-3 space-y-2">
                  <span className="block text-[11px] font-semibold text-slate-600">Buscar contato duplicado</span>
                  <div className="relative">
                    <div className="flex items-center gap-1.5 border border-slate-300 rounded px-2 py-1.5">
                      <Search size={12} className="text-slate-400" />
                      <input
                        type="text"
                        value={dupQuery}
                        onChange={(e) => setDupQuery(e.target.value)}
                        placeholder="Nome, email ou telefone…"
                        className="flex-1 outline-none text-sm"
                        autoComplete="off"
                      />
                      {searchingDup && <Loader2 size={12} className="animate-spin text-slate-400" />}
                    </div>
                    {dupResults.length > 0 && (
                      <ul className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded shadow max-h-72 overflow-y-auto">
                        {dupResults.map((c) => {
                          const cName = `${c.first_name || ''} ${c.last_name || ''}`.trim() || '(sem nome)';
                          const cEmp = c.empresas ? getCompanyDisplayName(c.empresas) : '—';
                          return (
                            <li key={c.id}>
                              <button
                                type="button"
                                onClick={() => { setDuplicate(c); setDupResults([]); setDupQuery(''); }}
                                className="w-full text-left px-2 py-1.5 hover:bg-slate-50 text-sm"
                              >
                                <div className="font-medium text-slate-800">{cName} · {cEmp}</div>
                                <div className="text-[10px] text-slate-500">{c.email || c.mobile || c.phone || '—'}</div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <ContactCard
                  label="Manter (master)"
                  contact={masterContact}
                  badge={<span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">absorve</span>}
                />
                {duplicate ? (
                  <ContactCard
                    label="Absorver (duplicata)"
                    contact={duplicate}
                    badge={
                      <button
                        type="button"
                        onClick={() => { setDuplicate(null); setCounts(null); }}
                        className="text-[10px] text-slate-500 hover:text-slate-800 flex items-center gap-0.5"
                      >
                        <X size={10} /> trocar
                      </button>
                    }
                  />
                ) : (
                  <div className="border border-dashed border-slate-300 rounded p-3 text-xs text-slate-500 italic flex items-center justify-center text-center">
                    Selecione a duplicata pelo campo de busca acima
                  </div>
                )}
              </div>

              {duplicate && (
                <>
                  {fieldsToFill.length > 0 && (
                    <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded p-2 text-[12px] text-emerald-900">
                      <div className="font-semibold mb-1">Campos vazios do master serão preenchidos:</div>
                      <ul className="space-y-0.5 list-disc list-inside">
                        {fieldsToFill.map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    </div>
                  )}

                  {counts && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2 text-[12px] text-amber-900">
                      <div className="font-semibold mb-1">Dependências da duplicata serão migradas:</div>
                      <ul className="grid grid-cols-2 gap-x-3">
                        <li>{counts.emails} email(s)</li>
                        <li>{counts.interacoes} interação(ões)</li>
                        <li>{counts.whatsapp} mensagem(ns) WhatsApp</li>
                        <li>{counts.employments} vínculo(s)</li>
                        <li>{counts.ofertas_negociador} oferta(s) (negociador)</li>
                      </ul>
                      <div className="mt-1 text-[10px] text-amber-700">+ outras tabelas com FK migradas atomicamente pela RPC</div>
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded p-2 text-[12px] text-red-800 flex items-start gap-1">
                  <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /> {error}
                </div>
              )}
            </>
          )}

          {report && (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-[12px] text-emerald-900">
                <div className="font-semibold mb-1">Merge concluído</div>
                <div className="text-[11px] mb-2">Master: {masterContact.id}<br />Duplicate: {duplicate?.id} (deletado)</div>
                <div className="font-semibold text-[11px]">Migrado:</div>
                <ul className="mt-1 grid grid-cols-2 gap-x-3 text-[11px]">
                  {report.migrated && Object.entries(report.migrated).map(([k, v]) => (
                    <li key={k} className={Number(v) > 0 ? 'font-medium' : 'text-slate-500'}>{k}: {String(v)}</li>
                  ))}
                </ul>
              </div>

              {duplicate?.empresa_id && duplicate.empresa_id !== masterContact.empresa_id && (
                <div className="bg-orange-50 border border-orange-200 rounded p-2.5 text-[12px] text-orange-900">
                  <div className="flex items-center gap-1.5 font-semibold mb-1">
                    <ArrowRightLeft size={12} /> Transferir master para a empresa da duplicata?
                  </div>
                  <div className="text-[11px] mb-2">
                    Master está em {getCompanyDisplayName(masterContact.empresas) || '—'}.
                    Mover para {getCompanyDisplayName(duplicate.empresas) || '—'} (com cargo "{duplicate.title || '—'}" e email "{duplicate.email || '—'}") fechará o vínculo antigo e abrirá o novo.
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleTransferAfter}
                      disabled={transferring}
                      className="px-2.5 py-1 text-[11px] bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      {transferring && <Loader2 size={11} className="animate-spin" />}
                      Sim, transferir
                    </button>
                    <button
                      type="button"
                      onClick={handleFinishWithoutTransfer}
                      disabled={transferring}
                      className="px-2.5 py-1 text-[11px] bg-white border border-orange-300 text-orange-800 rounded hover:bg-orange-100 disabled:opacity-50"
                    >
                      Não, manter na empresa atual
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-2 text-[12px] text-red-800">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {!report && (
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={merging}
              className="px-3 py-1.5 text-[12px] bg-white border border-slate-300 text-slate-700 rounded hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleMerge}
              disabled={merging || !duplicate}
              className="px-3 py-1.5 text-[12px] bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50 flex items-center gap-1"
            >
              {merging && <Loader2 size={12} className="animate-spin" />}
              {merging ? 'Mesclando…' : 'Confirmar merge'}
            </button>
          </div>
        )}
        {report && !duplicate?.empresa_id && (
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleFinishWithoutTransfer}
              className="px-3 py-1.5 text-[12px] bg-emerald-600 text-white rounded hover:bg-emerald-700"
            >
              Concluir
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MergeContactsModal;
